import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { toast } from 'react-toastify';
import {
  Save,
  Eye,
  EyeOff,
  Send,
  Image as ImageIcon,
  Clock,
  AlertCircle,
  X,
  Upload,
  Trash2,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { postService, aiService } from '../services/api';
import ImageUploader from './ImageUploader';
import TagSelector from './TagSelector';
import useAutoSave from '../hooks/useAutoSave';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import localImageService from '../services/localImageService';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Select } from './ui/select';
import { Textarea } from './ui/textarea';
import PageTitle from './PageTitle';

const PostEditor = ({ mode = 'create' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canCreateContent } = useAuth();

  const [post, setPost] = useState({
    title: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    featuredImageId: '', // ID da imagem local
    status: 'draft',
    visibility: 'public',
    tags: []
  });

  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [generatingExcerpt, setGeneratingExcerpt] = useState(false);

  // Auto-save
  const autoSave = useAutoSave(
    mode === 'edit' ? id : null,
    postService.autosavePost,
    {
      title: post.title,
      content: post.content,
      excerpt: post.excerpt
    },
    30000 // 30 segundos
  );

  // Verificar permissões
  useEffect(() => {
    if (!canCreateContent()) {
      navigate('/');
      return;
    }
  }, [canCreateContent, navigate]);

  const loadPost = useCallback(async () => {
    try {
      setLoading(true);

      // Buscar post diretamente pelo ID
      const response = await postService.getPostById(id);

      if (response.success) {
        const postToEdit = response.data.post;

        if (!postToEdit) {
          throw new Error('Post não encontrado');
        }

        // Verificar permissão para editar
        if (user.role !== 'admin' && postToEdit.authorId !== user.id) {
          throw new Error('Sem permissão para editar este post');
        }

        setPost({
          title: postToEdit.title,
          content: postToEdit.content,
          excerpt: postToEdit.excerpt || '',
          featuredImage: postToEdit.featuredImage || '',
          status: postToEdit.status,
          visibility: postToEdit.visibility,
          tags: postToEdit.tags || []
        });
      }
    } catch (error) {
      console.error('Erro ao carregar post:', error);
      alert('Erro ao carregar post: ' + error.message);
      navigate('/admin/posts');
    } finally {
      setLoading(false);
    }
  }, [id, user.id, user.role, navigate]);

  // Carregar post para edição
  useEffect(() => {
    if (mode === 'edit' && id) {
      loadPost();
    }
  }, [mode, id, loadPost]);

  const handleInputChange = (field, value) => {
    setPost(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro específico
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleImageUpload = (uploadedImage) => {
    // Agora as imagens são gerenciadas localmente, sem CORS
    const imageUrl = uploadedImage.url; // URL blob local

    if (showImageUploader === 'featured') {
      handleInputChange('featuredImage', imageUrl);
      // Armazenar também o ID da imagem para enviar metadados ao backend
      handleInputChange('featuredImageId', uploadedImage.id);
    } else {
      // Inserir no conteúdo markdown
      const imageMarkdown = `![${uploadedImage.originalName}](${imageUrl})`;
      const newContent = post.content + '\n\n' + imageMarkdown;
      handleInputChange('content', newContent);
    }

    setShowImageUploader(false);
  };

  const handleGenerateExcerpt = async () => {
    if (!post.content.trim()) {
      toast.error('Adicione conteúdo ao post antes de gerar o resumo');
      return;
    }

    if (post.content.length < 50) {
      toast.error('O conteúdo deve ter pelo menos 50 caracteres para gerar um resumo útil');
      return;
    }

    try {
      setGeneratingExcerpt(true);

      const data = await aiService.generateExcerpt(post.content, post.title);

      if (data.success) {
        handleInputChange('excerpt', data.data.excerpt);
        toast.success('Resumo gerado com sucesso!');
      } else {
        throw new Error(data.message || 'Erro ao gerar resumo');
      }

    } catch (error) {
      console.error('Erro ao gerar excerpt:', error);
      toast.error('Erro ao gerar resumo com IA: ' + (error.response?.data?.message || error.message));
    } finally {
      setGeneratingExcerpt(false);
    }
  };

  const validatePost = () => {
    const newErrors = {};

    if (!post.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    } else if (post.title.length < 3) {
      newErrors.title = 'Título deve ter pelo menos 3 caracteres';
    }

    if (!post.content.trim()) {
      newErrors.content = 'Conteúdo é obrigatório';
    } else if (post.content.length < 10) {
      newErrors.content = 'Conteúdo deve ter pelo menos 10 caracteres';
    }

    if (post.excerpt && post.excerpt.length > 500) {
      newErrors.excerpt = 'Resumo deve ter no máximo 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (statusToSave = 'draft') => {
    if (!validatePost()) return;

    // Coletar metadados das imagens usadas
    const imageMetadata = [];
    if (post.featuredImageId) {
      const metadata = localImageService.getImageMetadata(post.featuredImageId);
      if (metadata) {
        imageMetadata.push(metadata);
      }
    }

    // Não enviar blob URLs para o backend (causam erro de validação)
    const featuredImageForBackend = post.featuredImage && post.featuredImage.startsWith('blob:')
      ? undefined
      : post.featuredImage || undefined;

    const postData = {
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || undefined,
      featuredImage: featuredImageForBackend,
      status: statusToSave,
      visibility: post.visibility,
      tagIds: post.tags && post.tags.length > 0 ? post.tags.map(tag => tag.id).filter(id => id) : [],
      imageMetadata: imageMetadata // Metadados das imagens para o backend
    };

    try {
      setSaving(true);

      let response;

      if (mode === 'create') {
        response = await postService.createPost(postData);

        if (response.success) {
          // Redirecionar para edição do post criado
          navigate(`/admin/posts/edit/${response.data.post.id}`);
        }
      } else {
        response = await postService.updatePost(id, postData);
      }

      if (response.success) {
        const successMessage = `Post ${statusToSave === 'published' ? 'publicado' : 'salvo'} com sucesso!`;
        toast.success(successMessage, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        if (statusToSave === 'published') {
          navigate('/admin/posts');
        }
      } else {
        toast.error('Erro ao salvar: ' + response.message, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Erro ao salvar post:', error);
      console.error('Dados enviados:', postData);
      if (error.response?.data) {
        console.error('Resposta do servidor:', error.response.data);
        toast.error('Erro ao salvar: ' + (error.response.data.message || 'Erro desconhecido'), {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        toast.error('Erro ao salvar post', {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!validatePost()) return;

    try {
      setPublishing(true);

      if (mode === 'edit') {
        await handleSave('published');
      } else {
        // Para posts novos, salvar e publicar
        await handleSave('published');
      }
    } finally {
      setPublishing(false);
    }
  };

  const insertImageAtCursor = () => {
    setShowImageUploader('content');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-church-green mx-auto"></div>
          <p className="text-gray-400">Carregando post...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        title={mode === 'create' ? 'Criar Novo Post' : 'Editar Post'}
        description={mode === 'create' ? 'Crie um novo post' : `Editando: ${post.title}`}
      />
      <div className="container mx-auto px-4 py-12 max-w-7xl relative">
        {/* Header */}
        <Card className="mb-8 bg-[#0a0a0a] border-white/10 sticky top-24 z-30 shadow-2xl shadow-black/50">
          <CardHeader className="py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <CardTitle className="text-xl md:text-2xl text-white font-heading">
                  {mode === 'create' ? 'Criar Novo Post' : 'Editar Post'}
                </CardTitle>

                {/* Auto-save Indicator */}
                <div className="flex items-center space-x-4">
                  {autoSave.lastSaved && (
                    <div className="flex items-center text-xs text-gray-500">
                      {autoSave.saving ? (
                        <>
                          <Clock className="h-3 w-3 mr-1 animate-spin text-church-green" />
                          <span>Salvando...</span>
                        </>
                      ) : (
                        <>
                          <Clock className="h-3 w-3 mr-1" />
                          <span>
                            Salvo {formatDistanceToNow(autoSave.lastSaved, {
                              addSuffix: true,
                              locale: ptBR
                            })}
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  {autoSave.error && (
                    <Alert variant="destructive" className="p-1 px-2 h-auto">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      <AlertDescription className="text-xs">
                        {autoSave.error}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(!showPreview)}
                  size="sm"
                  className="border-white/10 text-gray-300 hover:text-white hover:bg-white/5 whitespace-nowrap"
                >
                  {showPreview ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Editar
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </>
                  )}
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => handleSave('draft')}
                  disabled={saving}
                  size="sm"
                  className="bg-white/10 text-white hover:bg-white/20 border-none whitespace-nowrap"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Salvando...' : 'Rascunho'}
                </Button>

                <Button
                  onClick={handlePublish}
                  disabled={publishing || saving}
                  size="sm"
                  className="bg-church-green text-white hover:bg-church-green/90 border-none shadow-lg shadow-church-green/20 whitespace-nowrap"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {publishing ? 'Publicando...' : 'Publicar'}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white font-medium">Título</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="text"
                  value={post.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Digite um título impactante..."
                  className={`text-xl font-semibold bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-church-green/50 h-14 ${errors.title ? 'border-red-500/50 focus:border-red-500' : ''}`}
                />
                {errors.title && (
                  <p className="text-sm text-red-500 mt-2">{errors.title}</p>
                )}
              </CardContent>
            </Card>

            {/* Content Editor */}
            <Card className="bg-[#0a0a0a] border-white/10 overflow-hidden">
              <CardHeader className="bg-white/5 border-b border-white/5 py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white font-medium">Conteúdo</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={insertImageAtCursor}
                    className="text-church-green hover:text-church-green/80 hover:bg-church-green/10"
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Inserir Imagem
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {showPreview ? (
                  <div className="prose prose-invert prose-lg max-w-none p-8 min-h-[500px] bg-[#0a0a0a]">
                    <MDEditor.Markdown source={post.content} style={{ backgroundColor: 'transparent', color: 'inherit' }} />
                  </div>
                ) : (
                  <div data-color-mode="dark" className="markdown-editor-wrapper">
                    <MDEditor
                      value={post.content}
                      onChange={(value) => handleInputChange('content', value || '')}
                      preview="edit"
                      hideToolbar={false}
                      height={600}
                      visibleDragbar={false}
                      style={{
                        backgroundColor: '#0a0a0a',
                        color: '#e5e7eb',
                        border: 'none',
                      }}
                      textareaProps={{
                        placeholder: 'Escreva seu estudo bíblico aqui... Use Markdown para formatar.',
                      }}
                    />
                  </div>
                )}
                {errors.content && (
                  <div className="p-4 border-t border-red-500/20 bg-red-500/5">
                    <p className="text-sm text-red-400">{errors.content}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publishing Options */}
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardHeader className="pb-3 border-b border-white/5">
                <CardTitle className="text-lg text-white font-medium">Configurações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-3">
                  <Label htmlFor="status" className="text-gray-300">Status</Label>
                  <div className="relative">
                    <select
                      value={post.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full h-10 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-church-green/20 focus:border-church-green/50 appearance-none"
                    >
                      <option value="draft" className="bg-[#0a0a0a]">Rascunho</option>
                      <option value="published" className="bg-[#0a0a0a]">Publicado</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="visibility" className="text-gray-300">Visibilidade</Label>
                  <div className="relative">
                    <select
                      value={post.visibility}
                      onChange={(e) => handleInputChange('visibility', e.target.value)}
                      className="w-full h-10 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-church-green/20 focus:border-church-green/50 appearance-none"
                    >
                      <option value="public" className="bg-[#0a0a0a]">Público</option>
                      <option value="private" className="bg-[#0a0a0a]">Privado</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Featured Image */}
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardHeader className="pb-3 border-b border-white/5">
                <CardTitle className="text-lg text-white font-medium">Imagem de Capa</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {post.featuredImage ? (
                  <div className="space-y-4">
                    <div className="relative rounded-lg overflow-hidden border border-white/10 group">
                      <img
                        src={post.featuredImage}
                        alt="Imagem destacada"
                        className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleInputChange('featuredImage', '')}
                          className="bg-red-500/80 hover:bg-red-600 rounded-full"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInputChange('featuredImage', '')}
                      className="w-full border-white/10 text-gray-400 hover:text-white hover:bg-white/5"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remover Imagem
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-church-green/50 transition-colors bg-white/5 cursor-pointer" onClick={() => setShowImageUploader('featured')}>
                    <Upload className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-400 font-medium">Clique para enviar</p>
                    <p className="text-xs text-gray-600 mt-1">16:9 Recomendado</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Excerpt */}
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardHeader className="pb-3 border-b border-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-white font-medium">Resumo</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleGenerateExcerpt}
                    disabled={generatingExcerpt || !post.content.trim()}
                    className="flex items-center gap-2 text-church-green hover:bg-church-green/10 h-8 px-2 text-xs"
                  >
                    <Sparkles className={`h-3 w-3 ${generatingExcerpt ? 'animate-spin' : ''}`} />
                    {generatingExcerpt ? 'Gerando...' : 'Gerar c/ IA'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 pt-6">
                <Textarea
                  value={post.excerpt}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  placeholder="Resumo curto que aparecerá no card..."
                  rows={4}
                  className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-church-green/50 resize-none ${errors.excerpt ? 'border-red-500/50' : ''}`}
                />
                <div className="flex justify-between items-center text-xs">
                  <span className={`${post.excerpt.length > 500 ? 'text-red-400' : 'text-gray-500'}`}>
                    {post.excerpt.length}/500
                  </span>
                  {post.content.trim() && post.content.length < 50 && (
                    <span className="text-yellow-600/80">
                      Mínimo para IA: 50
                    </span>
                  )}
                </div>
                {errors.excerpt && (
                  <p className="text-sm text-red-500">{errors.excerpt}</p>
                )}
              </CardContent>
            </Card>

            {/* Tags */}
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardHeader className="pb-3 border-b border-white/5">
                <CardTitle className="text-lg text-white font-medium">Tags</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <TagSelector
                  selectedTags={post.tags}
                  onTagsChange={(tags) => handleInputChange('tags', tags)}
                  allowCreate={true}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Image Upload Modal */}
        {showImageUploader && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <Card className="w-full max-w-lg bg-[#0a0a0a] border-white/10 shadow-2xl">
              <CardHeader className="border-b border-white/5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Upload de Imagem</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowImageUploader(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ImageUploader
                  onImageUploaded={handleImageUpload}
                  multiple={false}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <style jsx global>{`
      .w-md-editor {
        background-color: #0a0a0a !important;
        color: #e5e7eb !important;
        border: 1px solid rgba(255,255,255,0.1) !important;
        box-shadow: none !important;
      }
      .w-md-editor-toolbar {
        background-color: rgba(255,255,255,0.05) !important;
        border-bottom: 1px solid rgba(255,255,255,0.1) !important;
        color: #a3a3a3 !important;
      }
      .w-md-editor-toolbar li > button {
        color: #a3a3a3 !important;
      }
      .w-md-editor-toolbar li > button:hover, .w-md-editor-toolbar li > button.active {
        background-color: rgba(25, 130, 75, 0.2) !important;
        color: #fff !important;
      }
      .w-md-editor-preview {
        background-color: #0a0a0a !important;
        box-shadow: inset 0 0 10px rgba(0,0,0,0.5) !important; 
        border-left: 1px solid rgba(255,255,255,0.1) !important;
      }
      textarea {
        background-color: #0a0a0a !important;
        color: #e5e7eb !important;
      }
    `}</style>
    </>
  );
};

export default PostEditor;